import { GraphQLClient, gql } from 'graphql-request';

export const STAGING_GQL_ENDPOINT = 'https://graphql.staging.strava.com';
export const PROD_GQL_ENDPOINT = 'https://graphql.strava.com';
export const INTERNAL_GQL_ENDPOINT = 'https://graphql.dev.strava.com/';

/**
 * These *_INTERNAL_ROUTING_ENDPOINT variables should only be used on server side web app requests.
 * This allows the traffic to stay internal to our network, saving us money and latency by not having to
 * have our ALB and linkerd reprocess the request, but also allows us to forward the original request's
 * x-strava-* headers so that we can rate limit based on the true client IP as opposed to the IP of our
 * Mesos agents that the web server is running on.
 */
export const PROD_GQL_INTERNAL_ROUTING_ENDPOINT =
  'http://graphql.svc.strava.com/';
export const INTERNAL_GQL_INTERNAL_ROUTING_ENDPOINT =
  'http://internal.graphql.svc.strava.com/';
export const STAGING_GQL_INTERNAL_ROUTING_ENDPOINT =
  'http://staging.graphql.svc.strava.com/';

interface StravaGQLRequest extends Request {
  headers: {
    cookie: string;
    host: string;
  } & Request['headers'];
}

/**
 * get the proper auth cookie from the request object to ensure user is able to access the graph even when logged out.
 * @param {Object} req - request object from getServerSideProps
 * @param {bool} isStaging - true if it should use the staging cookie: _active4_session
 * @returns the constructed auth cookie string
 */
const getCookiesIncludingAuth = (req: StravaGQLRequest, isStaging: boolean) => {
  const cookies = req.headers ? req.headers.cookie : '';

  // get the staging or prod cookie
  const authCookie = isStaging ? '_active4_session' : '_strava4_session';

  // if the session cookie exists, pass the request cookies
  if (cookies && cookies.includes(authCookie)) {
    return cookies; // pass the cookies from server side
  }

  // if the auth cookie does not exist, the user is logged out, and missing a cookie
  return `${cookies}; ${authCookie}=true`;
};

/**
 * Checks to see if the strava auth cookie was set on the request.
 * @param {Object} req - request object from getServerSideProps
 * @param {bool} isStaging - true if it should use the staging cookie: _active4_session
 * @returns a boolean to denote whether the request has the auth cookie set.
 */
const hasActiveAuthCookieSet = (req: StravaGQLRequest, isStaging: boolean) => {
  const cookies = req.headers ? req.headers.cookie : '';

  // get the staging or prod cookie
  const authCookie = isStaging ? '_active4_session' : '_strava4_session';

  // if the session cookie exists return true else return false
  return cookies && cookies.includes(authCookie);
};

/**
 * Returns the correct GraphQL endpoint to used based on environment and whether
 * the Active session cookie was set.
 * @param {bool} hasAuth - true if the request had the Active session cookie set.
 * @param {bool} isStaging - true if the request was sourced in the staging environment.
 * @returns a boolean to denote whether the request has the auth cookie set.
 */
const getServerSideGraphqlGatewayEndpoint = (
  hasAuth: string | boolean,
  isStaging: boolean
) => {
  if (isStaging) {
    return STAGING_GQL_INTERNAL_ROUTING_ENDPOINT;
  }

  if (hasAuth) {
    /**
     * This endpoint allows the traffic to stay internal to our network and not have to
     * route back out through the public internet. This allows us to have some cost savings
     * as our public ingress does not have to reprocess the request, but also allows us to
     * forward on the various `x-strava-*` headers that have already been calculated and set
     * on the request. By doing this, we are able to successfully rate limit request by the
     * client's IP address as opposed to the web server's public IP that would otherwise be
     * used if we used the public facing production GraphQL Gateway URL.
     */
    return PROD_GQL_INTERNAL_ROUTING_ENDPOINT;
  }

  /**
   * THIS IS A TEMPORARY FALLBACK OPTION. Currently the Clients GraphQL Gateway has authorization
   * logic that rejects requests that do not come from a certain subset of application ids. This is
   * problematic for server side web app traffic because brand new strava viewers, first load
   * incognito viewers, or viewers who have just cleared their cookies will not have the required
   * strava session cookie set which means the request to the Clients GraphQL Gateway has no chance of
   * being in the subset of allowed application ids and will get rejected. Because of this we
   * will route the request to the Internal GraphQL Gateway endpoint while also forwarding on the original
   * request's x-strava-* headers. This is important because it will allow the correct client IP to be in
   * the Finagle context so that any downstream federated services that implement their own rate limiting
   * can rate limit the correct IP address. One implication of using the Internal GraphQL Gateway is that
   * the requests will NOT be rate limited at the gateway level (hence why this is not a long term solution).
   */
  return INTERNAL_GQL_INTERNAL_ROUTING_ENDPOINT;
};

/**
 * Returns object containing all x-strava-* headers from the req.headers
 * @param {object} req - request object from getServerSideProps
 * @returns {object} - Object containing all x-strava-* headers or empty object when there are not matches
 */
export const getStravaHeaders = (req: StravaGQLRequest) =>
  Object.keys(req?.headers ?? {})
    .filter((key) => /x-strava-/.test(key.toLowerCase()))
    .reduce((acc, key) => {
      acc[key] = req.headers[key as keyof typeof req.headers] as string;
      return acc;
    }, {} as Record<string, string>);

/**
 * use the built graphql client with its given configuration and parameters to call the graphql endpoint and return the data or error.
 * @param {GraphQLClient} gqlClient - the GQL client built with the desired configurations
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the response object from the graphql request
 */
const callWithClient = async (
  gqlClient: GraphQLClient,
  query: string,
  variables = {}
) => {
  const data = await gqlClient
    .request(
      gql`
        ${query}
      `,
      variables
    )
    .catch((e) => {
      /**
     * GraphQLClient JS Error shape example:
        ClientError: 'sampleMessage': {
          response: {
            errors: [ {message: "sampleMessage", extensions:{code:"FORBIDDEN"} ],
            status: 500,
            headers: Headers {}
          },
          request: {
            query:
              query Query {},
            variables: {}
          }
        }
     */
      const { errors: graphQLErrors, error: networkError } = e.response || {};

      const errorMessage =
        // error when calling the gql server
        networkError ||
        // errors with the gql query (ex: misspelled field name)
        (graphQLErrors && graphQLErrors[0]?.message) ||
        // error witht the client
        e.message ||
        // fallback error message
        'There was a problem with the GraphQL request';

      const errorOptions = graphQLErrors || e;

      throw new Error(errorMessage, errorOptions);
    });
  return data;
};

// The 'apollographql-client-name' request header is passed in with all
// client and server side requests. It allows us to accurately attribute
// graphql requests as having come from our various web apps.
// https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup
export const DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME = 'strava-web';
export const getApolloGraphqlClientName = () => {
  // Check process.env.APOLLO_GRAPHQL_CLIENT_NAME to see if a unique client
  // name is defined. If not, fall back to DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME.
  // This is wrapped in a try/catch so that in the case where process is not
  // defined, we will ignore the "process is not defined" error to keep it
  // from bubbling up to the client and blocking requests and just return
  // the default client name.
  try {
    const clientName = process.env.APOLLO_GRAPHQL_CLIENT_NAME;
    return clientName ?? DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
  } catch {
    // process isn't defined, ignore error
  }

  return DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
};

/**
 * makes a GraphQL request using the req object passed in from getServerSideProps
 * this is needed since the include credentials flag only works on the client side
 * should only be used when the call is made on the server side through getServerSideProps in NextJS.
 * @param {Object} req - the request object from getServerSideProps
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
export const gqlServerSideRequest = (
  req: StravaGQLRequest,
  query: string,
  variables = {}
) => {
  // call staging endpoint for localhost and staging
  const reqHost = req.headers.host;
  // webapp is included in this list for when we run the app using docker-compose and for Cypress tests
  const useStagingGql = ['staging', 'localhost', 'webapp', '::1'].some((host) =>
    reqHost.includes(host)
  );

  const hasAuth = hasActiveAuthCookieSet(req, useStagingGql);

  const endpoint = getServerSideGraphqlGatewayEndpoint(hasAuth, useStagingGql);

  const gqlClient = new GraphQLClient(endpoint, {
    headers: {
      Cookie: getCookiesIncludingAuth(req, useStagingGql),
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName(),
      ...getStravaHeaders(req)
    }
  });
  return callWithClient(gqlClient, query, variables);
};

/**
 * Calls the internal admin graph if the user has the proper credentials and if it is on admin.strava.com + behind VPN
 * Perfect for admin microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
export const gqlInternalRequest = (query: string, variables = {}) => {
  const { location } = window;

  const useStagingGql = location.hostname.match(/(staging)|(localhost)/g);

  const endpoint = useStagingGql ? STAGING_GQL_ENDPOINT : INTERNAL_GQL_ENDPOINT;

  const graphQLClient = new GraphQLClient(endpoint, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName()
    }
  });

  return callWithClient(graphQLClient, query, variables);
};

/**
 * Makes a graphql request on the client.
 * Infers the credentials from the browser to authenticate.
 * Perfect for athlete facing microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query
 */
export const gqlRequest = (query: string, variables = {}) => {
  const { location } = window;

  const useStagingGql = location.hostname.match(/(staging)|(localhost|::1)/g);

  const endpoint = useStagingGql ? STAGING_GQL_ENDPOINT : PROD_GQL_ENDPOINT;

  const graphQLClient = new GraphQLClient(endpoint, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName()
    }
  });

  return callWithClient(graphQLClient, query, variables);
};

export { gql };
